<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.driver.payoutCreditEntryList.modal.new.buttonText')"
      @click="modalOpen = true"
    />
    <export
      url="/payout_credit_entries/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <payout-credit-modal
    v-model:open="modalOpen"
    v-model:payoutCreditEntry="editPayoutCreditEntry"
    :driver="modelValue"
    @saved="refetch()"
  />

  <div class="ml-12">

    <payout-credit-debt-statistics-table :statistics="statistics" :loading="statisticsLoading" />

    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :actions="actions"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@components/utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {collOptionsProvider, ProviderParams, useProvider} from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {
  Driver,
  HydraCollection,
  PayoutCreditEntry,
} from '@/entities/Entities';
import { useVModel } from '@vueuse/core';
import { useFetchAction } from '@hooks/fetch';
import { useNotify } from '@utils/notifications';
import { useDelete } from '@hooks/api';
import { useQueryRef } from '@/hooks/url';
import PayoutCreditModal from '@pages/drivers/driver/PayoutCreditModal.vue';
import {Campaign, DriverComment, User} from '@/entities/Entities';
import {useTableFields} from '@hooks/table-configuration';
import PayoutCreditDebtStatisticsTable from '@utils/PayoutCreditDebtStatisticsTable.vue';

interface Props {
  modelValue: Driver | null;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const modalOpen = ref(false);

const editPayoutCreditEntry = ref<PayoutCreditEntry | undefined>(undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.payoutCreditDebt', {
    driver: props.modelValue?.user?.fullName,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  driver: props.modelValue?.id ?? '',
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const editableStatus = ['new'];

const [deletePayoutCreditEntry, { loading: loadingDelete }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const actions = [
  {
    title: t('pages.driver.payoutCreditEntryList.actions.edit'),
    action: (item: PayoutCreditEntry) => {
      editPayoutCreditEntry.value = { ...item };
      modalOpen.value = true;
    },
    condition: (item: PayoutCreditEntry) => editableStatus.includes(item.status),
  },
  {
    title: t('pages.driver.payoutCreditEntryList.actions.delete'),
    action: async (item: PayoutCreditEntry) => {
      if (await deletePayoutCreditEntry(`/payout_credit_entries/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
    condition: (item: PayoutCreditEntry) => editableStatus.includes(item.status),
  },
];

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'createdAt',
    title: t('date'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        label: t('date'),
      },
    },
  },
  {
    key: 'amount',
    unit: 'CHF',
    formatter: (v: number) => v.toFixed(2).padStart(5, '0') + ' CHF',
    title: t('payoutCreditEntry.amount'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        icon: <span>CHF</span>,
        label: t('payoutCreditEntry.amount'),
      },
    },
  },
  {
    key: 'debt',
    csvTranslations: {
      'true': t('payoutCreditEntry.debt'),
      'false': t('payoutCreditEntry.credit'),
    },
    formatter: (v: boolean) => v ? t('payoutCreditEntry.debt') : t('payoutCreditEntry.credit'),
    title: t('payoutCreditEntry.debtOrCredit'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'bool',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        labelTrue: t('payoutCreditEntry.debt'),
        labelFalse: t('payoutCreditEntry.credit'),
        label: t('payoutCreditEntry.debtOrCredit'),
      },
    },
  },
  {
    key: 'payoutEntry.payout.status',
    csvTranslations: {
      new: t('payoutCreditEntry.status.new'),
      pending: t('payoutCreditEntry.status.pending'),
      inPayment: t('payoutCreditEntry.status.inPayment'),
      payed: t('payoutCreditEntry.status.payed'),
      error: t('payoutCreditEntry.status.error'),
    },
    title: t('payoutCreditEntry.status.DEFAULT'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    computed: (v) => t(`payoutCreditEntry.status.${v.status}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        options: [
          'new',
          'pending',
          'inPayment',
          'payed',
          'error',
        ].map((item) => ({
          label: t(`payoutCreditEntry.status.${item}`),
          value: item
        })),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'new') {
            delete m['payoutEntry.payout.status'];
            m['exists[payoutEntry]'] = false;
          } else {
            delete m['exists[payoutEntry]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[payoutEntry]' in m) {
            return 'new';
          }
          return m[key];
        },
        label: t('payoutCreditEntry.status.DEFAULT'),
      },
    },
  },
  {
    key: 'payoutEntry.payout.campaignRegistration.campaign.group.name',
    title: t('payoutCreditEntry.paidInCampaign'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v?: string) => v ?? '-',
    to: (item) => item?.payoutEntry?.payout?.campaignRegistration?.campaign?.group?.id ?  ({ name: 'campaign-group-payouts', params: { campaignGroupId: item?.payoutEntry?.payout?.campaignRegistration?.campaign?.group?.id ?? '-' } }) : undefined,
    filter: {
      key: 'payoutEntry.payout.campaignRegistration.campaign.group',
      type: 'dropdown',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 500,
        createOption: false,
        resolveOnLoad: true,
        noOptionsText: t('inputSearchString'),
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
        label: t('payoutCreditEntry.paidInCampaign'),
      },
    },
  },
  {
    key: 'author.fullName',
    title: t('payoutCreditEntry.author'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    computed: (v: DriverComment) => v.author === null ? t('systemUser') : v.author?.fullName,
    filter: {
      key: 'author',
      type: 'dropdown',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        label: t('payoutCreditEntry.author'),
        searchable: true,
        clearOnSelect: true,
        options: async () => {
          let options = await collOptionsProvider<User>('/admins', {}, (value) => ({
            label: value.fullName,
            value: value.id,
          }))();
          options.push({
            label: t('systemUser'),
            value: 'null',
          });
          return options;
        },
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['author'];
            m['exists[author]'] = false;
          } else {
            delete m['exists[author]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[author]' in m) {
            return 'null';
          }
          return m[key];
        },
      },
    },
  },
  {
    key: 'subject',
    title: t('payoutCreditEntry.subject'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        label: t('payoutCreditEntry.subject'),
      },
    },
  },
  {
    key: 'comment',
    title: t('payoutCreditEntry.comment'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        label: t('payoutCreditEntry.comment'),
      },
    },
  },
  {
    key: 'onlyForCampaignRegistration.campaign.group.name',
    title: t('payoutCreditEntry.onlyForCampaignRegistration'),
    group: t('pages.driver.payoutCreditEntryList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v?: string) => v ?? '-',
    to: (item) => item?.onlyForCampaignRegistration?.campaign?.group?.id ?  ({ name: 'campaign-group-payouts', params: { campaignGroupId: item?.onlyForCampaignRegistration?.campaign?.group?.id ?? '-' } }) : undefined,
    filter: {
      key: 'onlyForCampaignRegistration.campaign.group',
      type: 'dropdown',
      group: t('pages.driver.payoutCreditEntryList.filterGroups.general'),
      options: {
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 500,
        createOption: false,
        resolveOnLoad: true,
        noOptionsText: t('inputSearchString'),
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
        label: t('payoutCreditEntry.onlyForCampaignRegistration'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const blockRequest = computed(() => !props.modelValue);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<PayoutCreditEntry>>('/payout_credit_entries', providerParams, {
  disabled: blockRequest,
  immediate: true,
});

const {
  data: statistics,
  loading: statisticsLoading,
} = useProvider<any>('/payout_credit_entries/statistics', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const notify = useNotify();

const loading = computed(() => loadingTable.value || loadingDelete.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
